import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useClerk, useUser } from "@clerk/clerk-react";
import { Bell, BookMarked, ChevronsUpDown, Globe, LogOut, Palette, Sparkles, UserCog, UserRound } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { AppearanceMenuItems } from "@/components/AppearanceMenu";
import {
  NOTIFICATION_DIALOG_TITLE,
  NotificationPreferencesDialog,
} from "@/components/layout/NotificationPreferencesDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenuButton, useSidebar } from "@/components/ui/sidebar";
import { useLanguage } from "@/hooks/useLanguage";
import type { Locale } from "@/lib/i18n";
import { logger } from "@/lib/sentry";

interface UserMenuProps {
  /** Shows "My account" (the /me portal) when the session may use it. */
  showMyAccount: boolean;
  /**
   * Shows "Notifications": the push preferences of the management app, whose
   * kinds are admin work (#1900).
   */
  showNotifications?: boolean;
}

/**
 * The user row at the bottom of the sidebar: one menu with the account, the
 * appearance and the language, so the footer holds two rows instead of four.
 */
export function UserMenu({ showMyAccount, showNotifications = false }: UserMenuProps) {
  const { i18n } = useLingui();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const clerk = useClerk();
  const { isMobile } = useSidebar();
  const language = useLanguage();

  const name = user?.fullName || user?.firstName || user?.username || "";
  const email = user?.primaryEmailAddress?.emailAddress ?? "";
  const initials = name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleSignOut = () => {
    clerk.signOut({ redirectUrl: "/" }).catch((error: unknown) => {
      logger.error("sign-out failed", { error });
    });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            size="lg"
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            aria-label={
              name
                ? i18n._({ id: "userMenu.openNamed", message: "Account menu: {name}" }, { name })
                : i18n._({ id: "userMenu.open", message: "Account menu" })
            }
          >
            <Avatar className="h-8 w-8 rounded-md">
              {user?.imageUrl ? <AvatarImage src={user.imageUrl} alt="" /> : null}
              <AvatarFallback className="rounded-md bg-primary text-primary-foreground text-xs font-semibold">
                {initials || <UserRound className="h-4 w-4" aria-hidden="true" />}
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
              <span className="truncate font-medium">
                {isLoaded ? name : i18n._({ id: "common.loading", message: "Loading..." })}
              </span>
              <span className="truncate text-xs text-sidebar-foreground/70">{email}</span>
            </div>
            <ChevronsUpDown className="ml-auto h-4 w-4 group-data-[collapsible=icon]:hidden" aria-hidden="true" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-56"
          side={isMobile ? "bottom" : "right"}
          align="end"
          sideOffset={4}
        >
          {showMyAccount ? (
            <DropdownMenuItem onSelect={() => navigate("/me")}>
              <UserRound className="mr-2 h-4 w-4" aria-hidden="true" />
              <Trans id="mePortal.userMenu.myAccount">My account</Trans>
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onSelect={() => navigate("/guide")}>
            <BookMarked className="mr-2 h-4 w-4" aria-hidden="true" />
            <Trans id="guide.title">Guide</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => navigate("/whats-new")}>
            <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />
            <Trans id="whatsNew.title">What's new</Trans>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => clerk.openUserProfile?.()}>
            <UserCog className="mr-2 h-4 w-4" aria-hidden="true" />
            <Trans id="userMenu.manageAccount">Manage login</Trans>
          </DropdownMenuItem>
          {showNotifications ? (
            <DropdownMenuItem onClick={() => setNotificationsOpen(true)}>
              <Bell className="mr-2 h-4 w-4" aria-hidden="true" />
              {i18n._(NOTIFICATION_DIALOG_TITLE)}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Palette className="mr-2 h-4 w-4" aria-hidden="true" />
              <Trans id="appearance.title">Appearance</Trans>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-48">
              <AppearanceMenuItems />
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Globe className="mr-2 h-4 w-4" aria-hidden="true" />
              <Trans id="userMenu.language">Language</Trans>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-44">
              <DropdownMenuRadioGroup
                value={language.current}
                onValueChange={(value) => void language.switch(value as Locale)}
              >
                {language.available.map((locale) => (
                  <DropdownMenuRadioItem key={locale} value={locale} lang={locale}>
                    {language.getLocaleName(locale)}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
            <Trans id="userMenu.signOut">Sign out</Trans>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {showNotifications ? (
        <NotificationPreferencesDialog open={notificationsOpen} onOpenChange={setNotificationsOpen} />
      ) : null}
    </>
  );
}
